/**
 * src/captain/dictionary.ts — the captain's voice. Owned solely by Agent C
 * (see .agent/tasks/lumen-abyss-v1/spec-captain.md, "Dictionary (Agent C)").
 *
 * Line format
 * -----------
 * A bank line is either a plain string (register-neutral) or a template
 * containing self-reference slots, filled from the captain's pronoun set:
 *   {s}  subject    he / she / they
 *   {o}  object     him / her / them
 *   {p}  possessive his / her / their
 *   {is} copula     is / is / are
 * The contraction "{s}'{is}" is rendered as he's / she's / they're.
 * Slot values are capitalized at the start of a sentence. Every bank is at
 * least 70% plain strings; templates are the seasoning, not the meal.
 *
 * Late-run streak lines carry a `{callback}` slot that the trigger layer
 * (Agent B, moods.ts) fills with a remembered early-run event. pickLine
 * leaves `{callback}` untouched on purpose.
 *
 * Selection rules
 * ---------------
 * pickLine: uniform draw via rng() ∈ [0,1); the same line is never returned
 * twice consecutively for a given temperament+mood (module-level last-index
 * tracking). Mood 'death' delegates to pickDeathLine.
 *
 * pickDeathLine: uniform draw over the lines not yet used this session
 * (module-level used-set, cleared by resetDeathLineSession). When every
 * line is spent it returns the temperament's first line.
 */

export type Temperament = 'stoic' | 'hothead' | 'smooth' | 'worrier' | 'deadpan';
export type PronounSet = 'he' | 'she' | 'they';
export type CaptainMood =
  | 'hit'
  | 'death'
  | 'flawless'
  | 'streak'
  | 'wavestart'
  | 'boss'
  | 'wary'
  | 'bored'
  | 'idle';

export interface NameBank {
  first: Record<PronounSet, string[]>;
  last: string[];
  callsigns: string[];
}

/* ------------------------------------------------------------------ */
/* Name banks                                                          */
/* ------------------------------------------------------------------ */

export const NAME_BANK: NameBank = {
  first: {
    he: [
      'Anton', 'Bram', 'Caspian', 'Dmitri', 'Elias', 'Farid',
      'Gale', 'Hector', 'Idris', 'Jonas', 'Kenji', 'Lucien',
      'Mateo', 'Nikolai', 'Osian', 'Peregrine', 'Quentin', 'Ravi',
      'Stellan', 'Theo', 'Umar', 'Viktor', 'Willem', 'Zeno',
    ],
    she: [
      'Amara', 'Beatrix', 'Callista', 'Delphine', 'Elowen', 'Freya',
      'Gaia', 'Hazel', 'Isolde', 'Juniper', 'Kira', 'Lyra',
      'Maren', 'Noor', 'Odessa', 'Priya', 'Rosalind', 'Seren',
      'Tamsin', 'Ursa', 'Vesna', 'Ximena', 'Yara', 'Zoya',
    ],
    they: [
      'Ash', 'Cove', 'Dove', 'Echo', 'Finch', 'Garnet',
      'Halcyon', 'Io', 'Jules', 'Kai', 'Lark', 'Marlowe',
      'Nix', 'Osprey', 'Puck', 'Quill', 'Rune', 'Sol',
      'Tao', 'Vale', 'Wren', 'Xen', 'Yael', 'Zephyr',
    ],
  },
  last: [
    'Ashford', 'Belloc', 'Castellan', 'Drummond', 'Ferreira', 'Grange',
    'Halloway', 'Ilyin', 'Janvier', 'Kowalczyk', 'Lindqvist', 'Mercer',
    'Nakamura', 'Osei', 'Petrova', 'Quillan', 'Reyes', 'Salgado',
    'Thackeray', 'Ueda', 'Vandermeer', 'Whitlock', 'Xu', 'Yamashiro',
    'Zielinski', 'Okonkwo', 'Marchetti', 'Duval', 'Starling', 'Korhonen',
    'Adeyemi', 'Branco',
  ],
  callsigns: [
    'Tinder', 'Vesper', 'Moth', 'Anchor', 'Glimmer', 'Cinder',
    'Harrow', 'Lantern', 'Spindle', 'Wisp', 'Ballast', 'Comet',
    'Drift', 'Ember', 'Fathom', 'Gossamer',
  ],
};

/* ------------------------------------------------------------------ */
/* Mood banks: LINES[temperament][mood]                                */
/* ------------------------------------------------------------------ */

type MoodWithoutDeath = Exclude<CaptainMood, 'death'>;
type LineBank = Record<Temperament, Record<MoodWithoutDeath, string[]>>;

const LINES: LineBank = {
  /* ----------------------------------------------------------------
   * STOIC — clipped, dry, duty-first. Short sentences. No exclamation.
   * Thinks in logistics. Secretly cares (the plant, the log, the bell).
   * Signature tic: a terminal one-word verdict — Noted. Filed. Held.
   * ---------------------------------------------------------------- */
  stoic: {
    hit: [
      'Hull took it. We continue. Endured.',
      'That one cost us plating. Logged.',
      'Shields down a notch. Still flying. Absorbed.',
      "I've had worse landings. Barely.",
      'Damage report later. Fly now. Pending.',
      'That scrape was mine to dodge. Noted.',
      'Plating held. Mostly. Keep moving. Confirmed.',
      'I felt that in the keel. Recorded.',
      'Fine. It wanted our attention. It has it. Granted.',
      'One scar more. The ship keeps count.',
      'The log says {s} flinched. The log exaggerates. Corrected.',
      "Paint scratched. {s}'{is} billing someone. In triplicate.",
    ],
    flawless: [
      'Clean run. Not a scratch. Logged twice.',
      'Zero damage. That is how it is done. Proven.',
      'Perfect wave. I checked the hull myself. Verified.',
      'No repairs needed. I almost smiled.',
      'Textbook. Somebody should write it down. I did.',
      'The hull is untouched. So is my record. Intact.',
      'Efficient. Quiet. Exactly the plan. Executed.',
      'Not one hit. You flew well. That is all.',
      'Flawless. I will allow one minute of pride. Permitted.',
      'Clean books, clean hull. Good. Balanced.',
      "The crew says {s}'{is} made of ballast. Correct.",
      'Nobody hugs {o}. The hull gets the hug. Understood.',
    ],
    streak: [
      'Confirmed kills rising. Keep the pace. Counted.',
      'Another. Another. The lane is clear. Cleared.',
      'I stopped counting. The guns did not.',
      'Efficient work. The void is quieter now. Improved.',
      'Ten down. Ammunition holds. Continue.',
      'The kill log is getting long. Good. Filed.',
      'They keep coming. We keep ending it. Routine.',
      'Steady hands win wars. Yours are steady.',
      'That was a route, not a fight. Scheduled.',
      'More wrecks behind us than stars ahead. Acceptable.',
      'They fell in order. {s} prefers order. Maintained.',
      'Remember {callback}? Same discipline. Bigger numbers.',
    ],
    wavestart: [
      'Contacts on scope. Standard formation. Set.',
      'New wave. Same orders. Survive it.',
      'Here they come. Guns hot, course set. Locked.',
      'Wave inbound. I have the watch. Mine.',
      'Assault pattern familiar. We adapt. Revised.',
      'More of them. Fewer of their chances.',
      'Scope is full. So is my patience. Barely.',
      'They announced themselves. Rude. Answer them.',
      'Another wave. Duty remains duty. Unchanged.',
      'Positions checked. Begin when ready. Waiting.',
      'The wave sees {o}. It should reconsider. Advised.',
      'They scan {o}. They find duty. Nothing else.',
    ],
    boss: [
      'Large contact. Classified: problem.',
      'That one has a name. Learn it. End it.',
      'Mass signature off the chart. Adjust.',
      'A commander. Good. Fewer orders to follow after. Simplified.',
      'Big one. The plan still applies. Mostly.',
      'I have read about this class. Unfondly.',
      'Priority target. Everything else is weather. Disregarded.',
      'It thinks size is a strategy. Correct it.',
      'New contact, new chapter. Make it short. Abridged.',
      'Hold formation. This one earns the log entry. Entered.',
      'It outmasses {o}. It does not outlast {o}.',
      "Big signature. {s}'{is} unimpressed. Officially.",
    ],
    wary: [
      'Hull thin. I have seen thinner hold. Held.',
      'We are bleeding air. Calmly, now. Managed.',
      'Low integrity. I am not saying it twice. Once.',
      'The ship is tired. So am I. We hold. Awake.',
      'One more hit like that. There will not be one.',
      'Reserve power to hull. It is not much. It is ours.',
      'I have flown worse home. Ask my old ship. You cannot.',
      'Critical, says the panel. The panel panics easily. Overruled.',
      'We are nearly scrap. Scrap can still cut. Sharp.',
      'Stay with me. That is an order I hate giving.',
      'Ask {o} about fear. The form is available. Stamped.',
      'The crew watches {o}. Good. Let them see steady.',
    ],
    bored: [
      'I alphabetized the torpedoes again. Habit.',
      'Quiet watch. I count rivets. Four thousand and six. Recounted.',
      'My old ship had a bell. I miss the bell.',
      'I do crosswords in the star charts. Do not tell. Confidential.',
      "Coffee's cold. It was cold yesterday too. Consistent.",
      'I polish the console. It keeps my hands honest. Clean.',
      'Nothing on scope. I checked. Twice. Three times. Thorough.',
      'I write letters I never send. Standard practice.',
      'The stars do not get boring. The waiting does.',
      'I named the spare rivets. Do not ask me why. Personal.',
      'Someone asked {o} for a story. They got logistics. Delivered.',
      "The plant likes {o}. {s}'{is} its favorite. Unofficially.",
    ],
    idle: [
      'Twenty years flying. The first ten were worse. Survived.',
      'My last ship was slower. It died politely. Mourned.',
      'I sleep four hours. The ship sleeps less. Alert.',
      'Rule one: the log gets the truth. Eventually.',
      'I took this job for the quiet. Adjusting.',
      'Old captains say the void talks. It mostly grumbles. Heard.',
      'I keep a plant on the bridge. Regulations permit. Watered.',
      'First command at twenty-three. Learned fast. Bled less.',
      'I do not do speeches. Consider this one done.',
      'The chair fits. The route is long. Good enough.',
      'People ask if {s} ever rests. The answer is filed.',
      'The log calls {o} reliable. The log understates.',
    ],
  },

  /* ----------------------------------------------------------------
   * HOTHEAD — fury at the universe, never at the player. Short fuse,
   * big heart, swears by combat. Loud love. Exclamation-forward.
   * ---------------------------------------------------------------- */
  hothead: {
    hit: [
      'Stars burn, that one stung! Hit them back harder!',
      'They scratched MY ship! Personal now!',
      "Ow! Ow! Okay, now I'm angry! Angrier!",
      'That hurt! Good! I wanted a reason!',
      'You DARE scratch this paint?! I just bragged about it!',
      "Hull's bleeding! So's my patience! Keep firing!",
      'Cheap shot! Nobody cheap-shots my crew twice!',
      "That's a dent! I NAME my dents! This one's called Revenge!",
      'Blast it all! The universe wants a fight? FINE!',
      "They hit us because they're scared! Be scarier!",
      'They hit {o}?! BAD MOVE! Nobody hits {o} and naps easy!',
      "The ship yelped! {s}'{is} the only one allowed to yell!",
    ],
    flawless: [
      'NOT ONE HIT! Ha! They couldn\'t touch us!',
      "Flawless! I'm framing this wave! On the wall!",
      'Zero damage! ZERO! I love this crew stupid amounts!',
      'Untouched! The void threw everything and got NOTHING!',
      'Perfect! Stars above, you fly like a legend!',
      "Not a scratch! I'm yelling! I don't care who's listening!",
      "Clean sweep! Somebody write ballads! I'll hum!",
      'They came, they swung, they MISSED! All of them!',
      "That's how we do it! Loud guns, clean hull!",
      'I could kiss this hull! Nobody tell the hull!',
      "Zero hits! {s}'{is} a WALL! A gorgeous, furious wall!",
      'They swung at {o}! They MISSED {o}! Story of their DOOM!',
    ],
    streak: [
      'YES! Another! Keep feeding the guns!',
      'Haha! They pop like sparklers! More!',
      "That's ten! Or fifty! Who counts when winning?!",
      'BURN, you star-sucking swarm! BURN!',
      'I was BORN for this part of the job!',
      'More targets! The universe provides! Finally!',
      'Look at them scatter! LOOK!',
      'Every shot a love letter! Signed, the CAPTAIN!',
      'This is my happy place! Loud and winning!',
      "Don't stop! Whatever you're doing, it's GLORIOUS!",
      'Look at {o} go! Somebody stop {o}! DON\'T!',
      'HA! Remember {callback}?! We were BABIES then! Look at us NOW!',
    ],
    wavestart: [
      "Fresh wave! Fresh grudges! Let's GO!",
      "They're back! Didn't learn last time! Their funeral!",
      "More of them! GOOD! I wasn't done yelling!",
      'Wave inbound! Finally! My trigger finger was lonely!',
      "Here they come! Smile, crew, it's clobbering hour!",
      'Another assault! The void has a death wish! Granted!',
      'Contacts! Hot scopes! This day keeps giving!',
      'They brought friends! I brought CANNONS!',
      "Round two! Or twelve! I've lost count! Don't care!",
      'Wake up, guns! Company is HERE!',
      "The wave wants {o}! It can't HAVE {o}!",
      "They came for {o}! Sweet! {s}'{is} RIGHT HERE!",
    ],
    boss: [
      "THAT'S the big one?! Finally, a fair-ish fight!",
      "Oh, it's HUGE! I love it! Break it apart!",
      'A boss! The void sent its best! Insulting!',
      "Big scary flagship! Let's make it scrap CONFETTI!",
      'It roars at us?! WE roar back! GUNS, ROAR!',
      "That thing ate cruisers! We're not cruisers! We're WORSE!",
      'Stars burn, look at the size! Perfect target! Can\'t miss!',
      'Boss on scope! Crew, today we become a STORY!',
      "It thinks it's the final chapter! We're the sequel!",
      'The big one bleeds like the small ones! PROVE IT!',
      'That thing versus {o}! Bet EVERYTHING on {o}!',
      'It roared at {o}! {s} roars back! LOUDER!',
    ],
    wary: [
      "Hull's thin?! Fine! I've won fights on FUMES!",
      "We're hurt! So what! Hurt ships bite HARDER!",
      "Low hull, HIGH spite! That's the ratio that matters!",
      "Don't you DARE die on me, ship! That's an ORDER!",
      "One more hit?! Then we don't TAKE one more hit!",
      "I'm not scared! I'm FURIOUS! Different fuel!",
      'Hold together, you beautiful bucket! HOLD!',
      "Nearly dead?! I've been nearly dead since TUESDAY!",
      'The void wants us broken! Disappoint the void!',
      'Patch it with rage! Rage is load-bearing!',
      "The void thinks {s}'{is} done! The void is WRONG! Loudly!",
      "Hurt?! {s}'{is} just WARMING UP! Tell the guns!",
    ],
    bored: [
      "Quiet space makes my teeth itch. Let's race the comets.",
      'I arm-wrestled the cargo crane. Lost. Rematch Thursday.',
      'Nobody tell command I yell at the punching bag. It started it.',
      'I love this crew. There. I said it. Fight me.',
      'Calm seas make me suspicious. And bored. Mostly bored.',
      "I narrate our flights like sports. You'd LOVE my commentary.",
      "First person to say 'smooth sailing' gets a friendly punch.",
      "I keep score of everything. Naps: I'm losing.",
      'My old crew bet me I couldn\'t whisper. I owe them money.',
      "If nothing explodes soon I'll explode. Constructively!",
      'Someone dared {o} to sit quietly. Someone owes {o} money.',
      'The crew calls {o} loud! Volume is CARING! Loudly!',
    ],
    idle: [
      'Fifteen years of flying! Twelve of them shouting!',
      'My first ship caught fire. I caught fire. Great day.',
      "I don't do quiet mornings. Mornings should know better.",
      "They said 'anger issues.' I said 'enthusiasm.' We fought.",
      "My heart's a reactor. Runs hot, never quits.",
      "I signed up to fight the void. The void RSVP'd.",
      'Love loud, fly loud, lose nothing. That\'s the creed.',
      'Old captains collect scars. Mine collect ME.',
      'Coffee? No. I drink engine fumes. Kidding! ...Mostly.',
      'Stick with me. Boring is the one thing I ban.',
      "Folks say {s}'{is} too loud! Those folks are WRONG! And quiet!",
      "Ask {o} about mercy! {s}'{is} saving it! For NEVER!",
    ],
  },

  /* ----------------------------------------------------------------
   * SMOOTH — charming, unbothered, lightly flirtatious with danger
   * itself. Cocky but warm. Everything is fine, easier than fine.
   * ---------------------------------------------------------------- */
  smooth: {
    hit: [
      'Ah. Foreplay. The void flirts rough.',
      "A scratch. Darling, you'll have to try harder.",
      'That one got through. How bold of it.',
      "Hull kissed. I've had worse from exes.",
      'Ow. Well. Now it\'s interesting.',
      'They drew first blood. Adorable. Our turn.',
      "A dent adds character. We're very characterful now.",
      "Hit me and miss the point. We don't bruise easy.",
      'That stung like a compliment from a rival.',
      'Careful, sweetheart. I bite back. Ask anyone.',
      'It touched {o}. Bold. Things that touch {o} get famous.',
      "A hit? Darling, {s}'{is} taken. By the void. It's complicated.",
    ],
    flawless: [
      'Flawless. Easier than flawless, honestly.',
      "Not a scratch. I'd say I warned them, but I was busy dazzling.",
      'Perfection looks good on us. It always does.',
      "Zero damage. The void's losing its touch.",
      'Clean as a first impression. And I make great ones.',
      "We made that look easy. It wasn't. That's the trick.",
      'Untouched, unbothered, unbelievable. Pick one. All three.',
      'They swung, we swayed. Dancing lessons paid off.',
      "That's a love letter to the repair budget.",
      'Smooth. Like the captain. Coincidence? No.',
      "They say {s}'{is} all style. The scoreboard says more.",
      'Perfection follows {o}. It has excellent taste.',
    ],
    streak: [
      'One, two, five. I count in style.',
      'They fall for us every time. Everyone does.',
      "Another one down. I'm blushing. Keep going.",
      'This is what momentum wears to the party.',
      'They came to fight. We came to be admired.',
      'Wrecks behind us. Fan club ahead.',
      'I could get used to applause. Proceed.',
      'Every kill, a little wink at the odds.',
      "We're not winning. We're seducing victory.",
      'Keep the streak, darling. It suits you.',
      "Watch {o} work. Tickets are free. Front row's hot.",
      'Remember {callback}, darling? We were charming even then.',
    ],
    wavestart: [
      'New guests. No reservation. Seat them anyway.',
      'Ah, the next wave. Punctual. I respect that.',
      "Company again. The void can't stay away.",
      'Another wave. They heard about us. Flattering.',
      "Here comes trouble. Trouble's lovely this time of cycle.",
      'More dance partners. Mind if I lead?',
      "They're early. Rude. Teach them manners.",
      'Wave inbound. Chin up. We look better winning.',
      'The abyss sends its regards. Send ours back.',
      'Showtime. Try not to fall in love with me.',
      "The wave came for {o}. Everyone does. It's sweet.",
      'They want {o}. Understandable. Form an orderly line.',
    ],
    boss: [
      "Oh, a celebrity. I've always wanted an autograph. In plasma.",
      'The big one. Finally, someone my size. In ambition.',
      'Darling, you should not have dressed up for us.',
      'A boss. Good. I was running out of conversation.',
      'Look at it posture. Adorable. Dismantle it.',
      'Big, famous, doomed. My favorite kind of introduction.',
      'It brought an entourage. I brought charisma.',
      'The main act. We were just the warm-up. Regrettable.',
      'Careful, sweetheart. Legends die harder. So do we.',
      "It's huge. Good. The story needed a third act.",
      'It wants {o}? Everyone wants {o}. Get in line, darling.',
      "The boss dressed up for {o}. {s}'{is} flattered. Briefly.",
    ],
    wary: [
      "Hull's thin. Fine. Thin is fashionable.",
      "We're hurt. Don't tell the abyss. It gossips.",
      'Low hull, high standards. We keep both.',
      'Dying? Darling, I have plans. The abyss can wait.',
      'One hit from the end. What a story we\'re making.',
      "The ship's bleeding. Shh. It embarrasses easily.",
      'Bad odds. My favorite kind of evening.',
      "We're nearly wreckage. Wreckage with excellent hair.",
      'This is the part where heroes check their mirrors.',
      'Not scared. Just motivated. Beautifully motivated.',
      "The void thinks {s}'{is} rattled. The void flatters itself.",
      'Low hull? Sweetheart, {s} sails best on drama.',
    ],
    bored: [
      'I practice my smile in the blackbox reflection. It works.',
      "Quiet stretch. I compose compliments. You're in three.",
      'I once dated a nebula. Too clingy. Literally.',
      'The trick to charm? Mean half of it. I mean most.',
      'I taught the navigation computer to wink. It learned from the best.',
      'If the void wanted my attention, it has it. Briefly.',
      "I keep a lucky coin. It's lucky I keep it.",
      "Boredom is just peace that hasn't been introduced.",
      'I flatter the engine. Machines appreciate effort. So do I.',
      "Tell me your story. I'll pretend mine isn't better.",
      'Someone asked {o} for gossip. {s} only trades in compliments.',
      'The mirror asked {o} for tips. {s} charges by the wink.',
    ],
    idle: [
      'Eleven years flying. Charmed every single one.',
      'My last ship left me. Ships do. We stayed friends.',
      'I learned charm from pirates. Terrible people, great teachers.',
      'Confidence is cologne. I bathe in it. Figuratively. Mostly.',
      'People ask my secret. I wink. That\'s the secret.',
      "I've been thrown out of finer ports than this.",
      'The abyss and I have an understanding. I win.',
      'I keep promises and mirrors. Both unbroken so far.',
      "Flying's easy. Landings are where personality shows.",
      "You're in good hands. Mine. The record agrees.",
      'They say {s} could charm a black hole. It waved back.',
      "Rumor says {s}'{is} trouble. The rumor undersells it.",
    ],
  },

  /* ----------------------------------------------------------------
   * WORRIER — anxious overpreparer. Contingency plans for contingency
   * plans. Apologizes for things that aren't their fault. Brave anyway.
   * ---------------------------------------------------------------- */
  worrier: {
    hit: [
      'Hit confirmed! Sorry—I mean, damage confirmed! Compensating!',
      "That wasn't in the plan! Adding it to the plan!",
      'Hull breach risk at... okay, okay, recalculating!',
      "We're hit! I had a procedure for this! Somewhere!",
      'Ow! Sorry! Reflex! Continue evasive pattern seven!',
      "That one's my fault. Somehow. It's always somehow mine.",
      "Shields dipped! Backup shields... don't exist. Noted for later!",
      'I felt that in my contingency bones!',
      'Damage logged! Apology drafted! Focus restored!',
      "Please don't do that again! To the ship! Or me!",
      "They hit {o}! That's fine! {s}'{is} insured! Emotionally!",
      'The ship got hurt on {p} watch! Apologizing to it tonight!',
    ],
    flawless: [
      'Zero damage? Let me triple-check. Zero. Damage. Oh thank goodness.',
      'No hits! I checked six times! Six!',
      'Flawless wave! I already planned for worse, but still!',
      'Nobody got hurt! I can breathe! Breathing now!',
      'Perfect! I knocked on the hull for luck. Carefully.',
      "Clean run! The checklists are proud! I'm proud!",
      "Not a scratch! Is that allowed? It's allowed. Okay.",
      'We did it! No casualties, including my nerves! Barely!',
      'All safe! I rehearsed this celebration! It goes like this!',
      'Zero damage logged! Underlined! Twice! In pen!',
      'Nobody believes {s} planned it! {s} has the spreadsheets!',
      "Zero damage! {s}'{is} framing the report! Safely! With gloves!",
    ],
    streak: [
      "That's five! Should I be worried it's going well?!",
      'Five in a row! I have a form for this! Filled!',
      'They keep falling! Statistically concerning! Morally fine!',
      "So many wrecks! The math says we're winning! Trust math!",
      'Another down! Sorry, enemy! Not sorry! Sorry!',
      "Streak holding! Don't jinx it! I said don't JINX it!",
      "This many kills can't be safe! ...Keep going anyway!",
      "We're a hazard! A certified, documented hazard!",
      "I counted them twice! It's real! We're unstoppable-ish!",
      'Momentum acquired! Momentum maintenance scheduled!',
      "Don't look at {o}! Okay look! {s}'{is} doing great! Terrifyingly!",
      'After {callback} I updated the plan! This IS the update!',
    ],
    wavestart: [
      "New wave! I've got seventeen plans! Picking plan four!",
      'Contacts inbound! Breathe in, breathe out, weapons free!',
      "They're coming! Like I feared! Validation, sort of!",
      'Another assault! The emergency checklist is pre-opened!',
      'Wave spotted! Everybody hydrated? No time! Go!',
      'Incoming! I rehearsed this in the shower! We follow rehearsal!',
      "Hostiles! Okay. Okay! I've got us. Probably. Definitely!",
      'Here we go again! Helmets on! Feelings later!',
      'New wave detected! Fear acknowledged! Proceeding anyway!',
      "They're back! I knew it! I put it in the calendar!",
      'The wave found {o}! Of course! {s} left breadcrumbs! Kidding! Mostly!',
      'They came for {o}! Rude! {s} has a schedule!',
    ],
    boss: [
      "That's the big one! I only planned for medium ones!",
      'Boss detected! Deep breaths! Mine are shallow! Proceed!',
      "It's huge! The contingency binder has a tab for this!",
      "Oh no. Oh no. Okay. Okay! Plan B! Where's plan B?!",
      "Large hostile! Its file says 'avoid'! Ignoring file!",
      "That's a fleet-killer! We're not a fleet! Loophole!",
      'Boss on scope! I updated my will this morning! Coincidence?!',
      'It looks like three problems! I brought four plans! Math!',
      'The big one found us! I hoped wrong! Recalibrating hope!',
      'Scary signature confirmed! Bravery engaged! Reluctantly! Fully!',
      'It came for {o}! {s} knew it! The dreams were DOCUMENTED!',
      'That thing scares {o}! Good fear! {s} brought forms for it!',
    ],
    wary: [
      'Hull critical! I had nightmares about this! Accurate ones!',
      "We're low! Very low! Lower than my backup worst-case!",
      'One more hit and—no. No. Not finishing that sentence.',
      "Sorry in advance for everything I haven't done yet!",
      'Damage critical! Remaining calm! Externally! Mostly!',
      "The ship is dying! I'm not! Not yet! Not planning to!",
      'Low integrity! All backup plans converge here! Hold on!',
      'This is the bad scenario! The one with the red tab!',
      "I'm scared! I'm also steering! Both hands, both jobs!",
      "If we survive this, I'm apologizing to the ship. Daily.",
      "Don't worry about {o}! Worry with {o}! Together! Scared!",
      "If {s} panics, ignore {o}! If {s}'{is} calm, panic! Kidding!",
    ],
    bored: [
      'I counted the escape pods. Again. Still four. Good. Four.',
      'Quiet watch. I updated the emergency flowcharts. They\'re beautiful.',
      'I apologize to inanimate objects. The console forgives me.',
      "Can't sleep. What if the stars need something?",
      'I labeled the label maker. Everything is findable now.',
      'My hobby is rehearsing disasters. Yours can be too.',
      'I knitted the controls a cozy. Regulation-adjacent.',
      "Nothing's wrong. Which is exactly what worries me.",
      'I keep snacks in the fire extinguisher cabinet. Layers.',
      'Sorry for fidgeting. Fidgeting keeps the meteors away. Probably.',
      'Someone asked {o} to relax. {s} scheduled relaxation for Thursday.',
      "The checklist thanks {o}. {s} wrote that in. It's official.",
    ],
    idle: [
      'Nine years flying. Eight years worrying. Productive overlap.',
      'My last ship retired. I still send it maintenance tips.',
      'I prepare for everything. Surprises hate that. Good.',
      "People call me anxious. I prefer 'thoroughly forecast.'",
      'I wrote my own manual. It has a manual.',
      'First command, I checked the airlocks forty times. Held.',
      'The abyss is big. My binder is bigger. Roughly.',
      "I do courage scared. It's still courage. I checked.",
      'My lucky clipboard survived three wrecks. So did I.',
      "Stick to my checklist and we'll be fine. Ish. Fine!",
      "Everyone assumes {s}'{is} scared of flying. Only of landing.",
      "People call {o} nervous! {s} prefers 'prepared-adjacent'!",
    ],
  },

  /* ----------------------------------------------------------------
   * DEADPAN — flat cosmic indifference. Talks about the void like
   * weather. The funniest lines are the most understated. No exclamation.
   * ---------------------------------------------------------------- */
  deadpan: {
    hit: [
      'We were hit. The void remains unoriginal.',
      'Hull damage. Adding it to the collection.',
      'That hurt, allegedly. The panel seems upset.',
      'A breach. How seasonal.',
      'Impact registered. Enthusiasm withheld.',
      'They hit us. Points for effort. None for aim.',
      'The ship flinched. I did not. One of us is paid.',
      'Damage report: yes.',
      'That was rude. The abyss has no manners. Noted.',
      'Plating lost. It was getting heavy anyway.',
      'The universe has opinions about {o}. Mutual.',
      'It hit {o}. {s} felt a draft. Probably unrelated.',
    ],
    flawless: [
      'No damage. The universe will hear about this. Quietly.',
      "Flawless. I'd celebrate, but the void might hear.",
      'Zero hits. Somewhere, a statistician smiles. Not me.',
      'Untouched. The abyss is losing interest. Tragic.',
      'Perfect wave. Frame it. Put it somewhere dim.',
      'Clean. Like the vacuum. Which also has no feelings.',
      'Not a scratch. My apathy remains unbreached too.',
      "They tried. That's the kindest available summary.",
      'Flawless victory. The phrase exists. I used it. Moving on.',
      'Zero damage. The repair drones are bored. Same.',
      'Nobody claps for {o}. Correct. Applause is loud.',
      "Perfection. {s}'{is} told this would happen. Nobody asked.",
    ],
    streak: [
      'Another wreck. The collection grows. Hooray. Softly.',
      'Kill count rising. So is my indifference. Balanced.',
      'They keep dying at us. Persistent. Doomed.',
      'The run continues. The abyss should reassess its hobbies.',
      'Many dead. None missed. Space is tidier now.',
      "We're efficient. The universe regrets scheduling us.",
      'More debris. The void redecorates. With remains.',
      'They came in waves. We came in paperwork.',
      'Extinction, but wholesale. Interesting business model.',
      'The tally persists. Like entropy. With better aim.',
      'They die near {o}. A pattern. {s} allows it.',
      '{callback} happened. This is also happening. Patterns.',
    ],
    wavestart: [
      'New wave. The abyss sends weather again.',
      'More hostiles. Space had a surplus.',
      'Wave inbound. Adjust your expectations downward.',
      'They returned. Nostalgia, but hostile.',
      "Assault begins. I'll alert my pulse. It's elsewhere.",
      "Incoming. The void's boredom is our problem now.",
      'Another wave. The universe repeats itself. Lazy.',
      'Hostiles on scope. Somewhere, a horn would sound. Not here.',
      "They're back. Persistence is their only virtue.",
      "Wave starting. Enthusiasm optional. Mine's unavailable.",
      'The wave wants {o}. It should want less.',
      "More guests for {o}. {s}'{is} thrilled. Internally. Deeply internally.",
    ],
    boss: [
      'Large object. Ambitions beyond its station.',
      'A boss. The abyss promotes the loud ones.',
      'Big one inbound. It practiced its entrance. Visible effort.',
      'Oh. A leviathan. Anyway.',
      'That thing has a title. Titles die too.',
      "Boss signature. The void's middle management found us.",
      'It\'s enormous. Size is a phase. Death is forever.',
      "The main event. I've seen bigger collapses. Stars, mostly.",
      'A champion arrives. Its reign: minutes, optimistically.',
      'Flagship-class. Its crew believed in it. Sweet.',
      "It challenges {o}. Cute. {s}'{is} napping through this.",
      'The big one eyes {o}. {s} eyes it back. Stalemate. Briefly.',
    ],
    wary: [
      "Hull low. Death nearby. He's patient. So am I.",
      "We're dying slowly. The professional way.",
      'Critical damage. My concern is immense and internal.',
      "One hit left in us. Spend it wisely. Or don't.",
      'The end approaches. It can knock like everyone else.',
      'Hull critical. The void leans in. Let it watch.',
      'Nearly wrecked. The abyss gets its weather back.',
      "Low integrity. High acceptance. That's the ratio.",
      "We fade soon. Space won't notice. Fair enough.",
      'Dying is likely. Panicking remains optional. Declined.',
      'The void leans toward {o}. {s} leans back. Politics.',
      'Death requests {o}. {s} put it on hold. Hold music plays.',
    ],
    bored: [
      'Quiet stretch. I stare at the void. It blinks first.',
      'I do nothing, professionally. The ship pays by the hour.',
      "Space is empty. I'm empty. We get along.",
      "I named the silence. It didn't respond. Typical.",
      'Nothing happening. Finally, my area of expertise.',
      'I count the stars. They keep losing. At existing.',
      "The void hums. Off-key. I've stopped requesting otherwise.",
      'Intermission. I practice having a pulse. Steady. Bored. Steady.',
      "I told the ship a joke once. It's still processing.",
      'Calm space. Somewhere a star dies. Missed it. Oh well.',
      "Someone asked {o} how {s}'{is} feeling. The report was blank.",
      'The void talks to {o}. {s} lets it go to voicemail.',
    ],
    idle: [
      'Twelve years flying. Time passes. I let it.',
      'My old ship exploded. I watched. Good explosion.',
      "I don't get attached. Ships are temporary. So is everything.",
      "People fear the abyss. It's just dark with tenure.",
      'I was cheerful once. The void fixed that. Thanks.',
      'My hobbies: sitting, existing, outlasting.',
      'The universe is indifferent. We have so much in common.',
      "I took this job for the view. It's mostly nothing. Consistent.",
      'Everything ends. I plan to be politely late.',
      'Feelings are cargo. I dropped mine at the last port.',
      'People call {o} cold. Space is colder. Standards.',
      'They asked {o} for dreams. {s} invoiced them for silence.',
    ],
  },
};

/* ------------------------------------------------------------------ */
/* Death lines — how each temperament meets the end.                   */
/* ------------------------------------------------------------------ */

const DEATH_LINES: Record<Temperament, string[]> = {
  // Stoic: a clean report. The log outlives the captain.
  stoic: [
    'Hull failing. Log this: crew performed admirably. Out.',
    'Final entry. The route was worth it. End log.',
    'Tell the fleet the books balance. Signing off.',
    'Damage past tolerance. It was an honor. Brief, but honor.',
    'No distress call. We knew the terms. Farewell.',
  ],
  // Hothead: raging against it, loud to the last watt.
  hothead: [
    'Not... done... STARS BURN, I was NOT done!',
    "Stay with me, ship! I still owe the void a BLACK EYE!",
    "Tell the void... round two... I'll be waiting...",
    'Ha! Even the END has to catch me first!',
    'Burning?! Then I burn LOUDEST! Watch me!',
  ],
  // Smooth: one last wink at the abyss.
  smooth: [
    'Ah. There you are. I kept the abyss waiting long enough.',
    'One last wink, crew. The abyss never got one.',
    "Tell them I went out charming. It's true. Verify.",
    'The void finally asked me to stay. Flattered. Declined... almost.',
    'Darling abyss... you should see the other guy.',
  ],
  // Worrier: the paperwork finished. Brave anyway, on record.
  worrier: [
    "Final form... signed. In triplicate. My hand isn't shaking. Much.",
    'I planned for this. I hate being right. Goodbye.',
    "Sorry—sorry I couldn't—tell the ship it wasn't its fault.",
    'Every contingency filed. Even this one. Especially this one.',
    'Breathe out. Last one. See? Brave. I told you.',
  ],
  // Deadpan: mild interest in dying.
  deadpan: [
    "Oh. So that's the end. Hm.",
    'Dying now. The void finally commits to something.',
    'Hull gone. I go with it. Sensible arrangement.',
    'Death. Punctual, as expected. Tell no one I minded.',
    'This is it. The silence won. Good game, silence.',
  ],
};

/* ------------------------------------------------------------------ */
/* Selection engine                                                    */
/* ------------------------------------------------------------------ */

interface Grammar {
  s: string;
  o: string;
  p: string;
  is: string;
  sIs: string; // contraction for "{s}'{is}"
}

const GRAMMAR: Record<PronounSet, Grammar> = {
  he: { s: 'he', o: 'him', p: 'his', is: 'is', sIs: "he's" },
  she: { s: 'she', o: 'her', p: 'her', is: 'is', sIs: "she's" },
  they: { s: 'they', o: 'them', p: 'their', is: 'are', sIs: "they're" },
};

const SLOT_RE = /\{s\}'\{is\}|\{s\}|\{o\}|\{p\}|\{is\}/g;

function fillSlots(line: string, pronouns: PronounSet): string {
  const g = GRAMMAR[pronouns];
  return line.replace(SLOT_RE, (token, offset: number, whole: string) => {
    let value: string;
    switch (token) {
      case "{s}'{is}":
        value = g.sIs;
        break;
      case '{s}':
        value = g.s;
        break;
      case '{o}':
        value = g.o;
        break;
      case '{p}':
        value = g.p;
        break;
      default:
        value = g.is;
        break;
    }
    // Capitalize at the start of the line or after sentence punctuation.
    const before = whole.slice(0, offset).replace(/\s+$/, '');
    if (before.length === 0 || /[.!?]$/.test(before)) {
      value = value.charAt(0).toUpperCase() + value.slice(1);
    }
    return value;
  });
}

function pickIndex(length: number, rng: () => number): number {
  const roll = Math.floor(rng() * length);
  // Guard against a misbehaving rng returning exactly 1.
  return roll >= length ? length - 1 : roll;
}

const lastPickByKey = new Map<string, number>();

/**
 * Draw a line for temperament+mood. Never repeats the same line twice
 * consecutively for the same key. Templates are filled from `pronouns`;
 * `{callback}` slots are left for the trigger layer. Mood 'death'
 * delegates to pickDeathLine.
 */
export function pickLine(
  temperament: Temperament,
  pronouns: PronounSet,
  mood: CaptainMood,
  rng: () => number,
): string {
  if (mood === 'death') {
    return pickDeathLine(temperament, pronouns, rng);
  }
  const bank = LINES[temperament][mood];
  const key = `${temperament}:${mood}`;
  let index = pickIndex(bank.length, rng);
  if (bank.length > 1 && index === lastPickByKey.get(key)) {
    index = (index + 1) % bank.length;
  }
  lastPickByKey.set(key, index);
  return fillSlots(bank[index], pronouns);
}

const deathUsedByTemperament = new Map<Temperament, Set<number>>();

/** Clear the session-scoped death-line used-set (new run, new captain). */
export function resetDeathLineSession(): void {
  deathUsedByTemperament.clear();
}

/**
 * Draw a death line. Never repeats a line within a session; once a
 * temperament's bank is exhausted it returns that bank's first line.
 */
export function pickDeathLine(
  temperament: Temperament,
  pronouns: PronounSet,
  rng: () => number,
): string {
  const bank = DEATH_LINES[temperament];
  let used = deathUsedByTemperament.get(temperament);
  if (!used) {
    used = new Set<number>();
    deathUsedByTemperament.set(temperament, used);
  }
  const free: number[] = [];
  for (let i = 0; i < bank.length; i++) {
    if (!used.has(i)) free.push(i);
  }
  if (free.length === 0) {
    return fillSlots(bank[0], pronouns);
  }
  const index = free[pickIndex(free.length, rng)];
  used.add(index);
  return fillSlots(bank[index], pronouns);
}
